import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Stack, Typography } from '@mui/material';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import CustomButton from 'components/button/CustomButton';
import LabelChip from 'components/chip/LabelChip';
import ConditionList from 'components/condition-list/ConditionList';
import ListEmptyState from 'components/condition-list/ListEmptyState';
import { ListPanelItem } from 'components/condition-list/ListPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  ExpenseLabel,
  createExpenseLabel,
  deleteExpenseLabel,
  listExpenseLabels,
  updateExpenseLabel,
} from 'stores/ExpenseStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import ExpenseLabelForm, { ExpenseLabelFormValues } from './components/ExpenseLabelForm';

const NAME_MAX = 64;
const DEFAULT_COLOR = '#ef6c00';

type Mode = 'none' | 'create' | 'detail' | 'edit';

const searchConditions: Condition[] = [
  { key: 'name', name: 'ラベル名', type: 'text', color: '#ef6c00' },
];

const emptyValues: ExpenseLabelFormValues = { name: '', color: DEFAULT_COLOR };

/**
 * 品目ラベル管理（パターンA・3ペイン）。
 * 左=ラベル名+色Chip / 右=名前・色編集。作成/更新/削除（論理削除・確認ダイアログ）。
 */
const ExpenseLabelManagement: React.FC = () => {
  const [labels, setLabels] = React.useState<ExpenseLabel[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [keyword, setKeyword] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | undefined>();
  const [mode, setMode] = React.useState<Mode>('none');
  const [values, setValues] = React.useState<ExpenseLabelFormValues>(emptyValues);
  const [nameError, setNameError] = React.useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const fetchLabels = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await listExpenseLabels();
      setLabels(list);
    } catch (e) {
      errorDefault('品目ラベルの取得に失敗しました', toErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const selected = React.useMemo(
    () => labels.find((l) => l.expenseLabelId === selectedId),
    [labels, selectedId],
  );

  const filtered = React.useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return labels;
    return labels.filter((l) => l.name.toLowerCase().includes(kw));
  }, [labels, keyword]);

  const items: ListPanelItem[] = React.useMemo(
    () => filtered.map((l) => ({ id: l.expenseLabelId, primary: l.name })),
    [filtered],
  );

  const handleSearch = (filters: Filter[]) => {
    const nameFilter = filters.find((f) => f.key === 'name');
    setKeyword(nameFilter?.value.key ?? '');
  };

  const handleSelect = (item: ListPanelItem) => {
    setSelectedId(item.id);
    setMode('detail');
    setNameError(undefined);
  };

  const handleCreateClick = () => {
    setSelectedId(undefined);
    setValues({ ...emptyValues });
    setNameError(undefined);
    setMode('create');
  };

  const handleEditClick = () => {
    if (!selected) return;
    setValues({ name: selected.name, color: selected.color });
    setNameError(undefined);
    setMode('edit');
  };

  const validate = (): boolean => {
    if (!values.name.trim()) {
      setNameError(MSG_REQUIRED('ラベル名'));
      return false;
    }
    if (values.name.length > NAME_MAX) {
      setNameError(MSG_MAX_LENGTH('ラベル名', NAME_MAX));
      return false;
    }
    setNameError(undefined);
    return true;
  };

  /** 422（有効な同名重複）をフォームにインライン表示。それ以外は共通処理へ委譲。 */
  const handleSaveError = (e: unknown, fallbackMsg: string) => {
    const status = (e as { status?: number })?.status;
    if (status === 422) {
      setNameError('同じ名前の品目ラベルが既に存在します');
      return;
    }
    if (status === 403 || status === 404) {
      errorDefault('対象の品目ラベルが見つかりません', toErrorDetail(e));
      fetchLabels();
      setMode('none');
      setSelectedId(undefined);
      return;
    }
    errorDefault(fallbackMsg, toErrorDetail(e));
  };

  const handleCreate = async () => {
    if (!validate()) return;
    try {
      const id = await createExpenseLabel({ name: values.name.trim(), color: values.color });
      successDefault('品目ラベルを作成しました');
      // 論理削除済み同名の「復活」もありうるため一覧を再取得して最新状態を反映
      await fetchLabels();
      setSelectedId(id);
      setMode('detail');
    } catch (e) {
      handleSaveError(e, '品目ラベルの作成に失敗しました');
    }
  };

  const handleUpdate = async () => {
    if (!selected || !validate()) return;
    try {
      await updateExpenseLabel({
        expenseLabelId: selected.expenseLabelId,
        name: values.name.trim(),
        color: values.color,
      });
      successDefault('品目ラベルを更新しました');
      await fetchLabels();
      setMode('detail');
    } catch (e) {
      handleSaveError(e, '品目ラベルの更新に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteExpenseLabel({ expenseLabelId: selected.expenseLabelId });
      successDefault('品目ラベルを削除しました');
      setDeleteOpen(false);
      setSelectedId(undefined);
      setMode('none');
      await fetchLabels();
    } catch (e) {
      setDeleteOpen(false);
      const status = (e as { status?: number })?.status;
      if (status === 403 || status === 404) {
        errorDefault('対象の品目ラベルが見つかりません', toErrorDetail(e));
        fetchLabels();
        setSelectedId(undefined);
        setMode('none');
        return;
      }
      errorDefault('品目ラベルの削除に失敗しました', toErrorDetail(e));
    }
  };

  const renderItem = (item: ListPanelItem) => {
    const label = labels.find((l) => l.expenseLabelId === item.id);
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', minWidth: 0 }}
      >
        <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
          {item.primary}
        </Typography>
        {label && <LabelChip name={label.name} color={label.color} size="small" />}
      </Stack>
    );
  };

  const detailFooter =
    mode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setMode('none')}
        />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={handleCreate}
          disabled={values.name.trim() === ''}
        />
      </Stack>
    ) : mode === 'edit' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setMode('detail')}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={handleUpdate}
          disabled={values.name.trim() === ''}
        />
      </Stack>
    ) : mode === 'detail' && selected ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="削除"
          buttonVariant="danger"
          icon={<DeleteIcon />}
          onClick={() => setDeleteOpen(true)}
        />
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={handleEditClick}
        />
      </Stack>
    ) : undefined;

  const detailContent =
    mode === 'create' || mode === 'edit' ? (
      <ExpenseLabelForm values={values} onChange={setValues} nameError={nameError} />
    ) : mode === 'detail' && selected ? (
      <ExpenseLabelForm
        values={{ name: selected.name, color: selected.color }}
        onChange={() => {}}
        disabled
      />
    ) : null;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5 }}>
      <DomainBreadcrumbs
        items={[{ label: '経費管理', to: '/expense' }, { label: '品目ラベル管理' }]}
      />
      <ConditionList
        conditions={searchConditions}
        items={items}
        selectedId={selectedId}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onCreateClick={handleCreateClick}
        createButtonLabel="品目ラベルを作成"
        renderItem={renderItem}
        isProgress={loading}
        detailFooter={detailFooter}
        detailIsEmpty={mode === 'none'}
        detailEmptyState={
          <ListEmptyState
            title="品目ラベルを選択してください"
            description="左のリストから選ぶか、＋から新規作成できます"
          />
        }
        listEmptyState={
          <ListEmptyState
            title="品目ラベルがありません"
            description="＋から品目ラベルを作成してください"
          />
        }
      >
        {detailContent}
      </ConditionList>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="品目ラベルの削除"
        confirmLabel="削除"
      >
        <Typography variant="body2">
          「{selected?.name}
          」を削除します。既に登録済みの経費の表示には残りますが、今後の選択肢からは除外されます。
        </Typography>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default ExpenseLabelManagement;
